import type { ReactNode } from 'react';

/**
 * En-tête d'écran (refonte éditoriale).
 *
 * ### Le constat
 *
 * Aucun écran n'avait d'en-tête. Chacun empilait, dans un ordre qui variait
 * d'un fichier à l'autre : un lien « ← Bibliothèque », un `h1` nu, parfois un
 * bandeau, puis les contrôles, puis le contenu. Trois conséquences, toutes
 * visibles sur les captures :
 *
 * - **le lien de retour n'a pas de sens sur un onglet de premier niveau.**
 *   Le Journal, les Courses et la Semaine sont des destinations de la barre
 *   de navigation ; leur mettre « ← Bibliothèque » en haut, c'est proposer de
 *   remonter à un parent qui n'existe pas. Il est ici **facultatif**, et
 *   réservé aux écrans qui sont réellement enfants d'un autre ;
 * - **le titre et les actions ne se rencontraient jamais.** « Semaine » à
 *   gauche, « Semaine précédente / suivante » ailleurs, sans ligne commune,
 *   alors que ce sont les commandes de ce titre-là ;
 * - **rien ne disait de quoi l'écran parle.** Un titre de deux mots suivi
 *   directement d'un champ de recherche laisse la première phrase de la page
 *   au hasard.
 *
 * ### La forme
 *
 * Un repère (`eyebrow`), un titre, un chapeau facultatif, et les actions de
 * l'écran sur la même ligne que le titre dès qu'il y a la place. Un filet
 * ferme l'en-tête : c'est ce qui, dans un livre, sépare le titre du texte.
 */
export interface PageHeaderProps {
  /**
   * Repère au-dessus du titre : la section dont cet écran fait partie
   * (« Le livre de famille », « Cette semaine »). Court, jamais une phrase.
   */
  eyebrow?: ReactNode;
  title: ReactNode;
  /** Une phrase qui dit de quoi il retourne. Facultative — mais préférable à rien. */
  lede?: ReactNode;
  /** Lien de retour. **Uniquement** si l'écran est enfant d'un autre. */
  back?: ReactNode;
  /** Actions de l'écran, alignées à droite du titre sur grand écran. */
  actions?: ReactNode;
  /**
   * Le titre de cet écran est-il déjà porté par la barre de navigation ?
   * (US-105)
   *
   * Quand il l'est — la bibliothèque, dont l'onglet « Recettes » est actif et
   * visible en permanence —, le titre reste dans le document mais **cesse
   * d'être peint** sous grand écran : trois fois le même mot (la marque,
   * le titre, l'onglet actif) coûtaient un tiers du premier écran d'un
   * téléphone pour ne rien apprendre. Il réapparaît à partir de `lg`, où la
   * navigation devient horizontale et où la place ne manque plus.
   *
   * `sr-only` et non un titre retiré : un document sans `h1` se parcourt mal
   * au lecteur d'écran, et c'est exactement le défaut qu'US-56 avait corrigé.
   */
  titrePorteParLaNavigation?: boolean;
  /** Contenu placé sous le filet — barre d'outils, onglets, filtres. */
  children?: ReactNode;
  className?: string;
}

export function PageHeader({
  eyebrow,
  title,
  lede,
  back,
  actions,
  titrePorteParLaNavigation = false,
  children,
  className = '',
}: PageHeaderProps): React.JSX.Element {
  return (
    <header className={`flex flex-col gap-4 ${className}`.trim()}>
      {back !== undefined && <div className="flex">{back}</div>}

      {/*
        `items-end` et non `items-center` : les actions s'alignent sur la
        ligne de base du titre, pas sur son milieu. Avec un titre sur deux
        lignes — ce qui arrive dès le format téléphone — un alignement centré
        fait flotter les boutons au milieu du titre.
      */}
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between md:gap-6">
        <div className={`flex min-w-0 flex-col gap-2 ${titrePorteParLaNavigation ? 'sr-only lg:not-sr-only' : ''}`.trim()}>
          {eyebrow !== undefined && <p className="text-label">{eyebrow}</p>}
          <h1 className="text-heading-1">{title}</h1>
          {lede !== undefined && <p className="max-w-reading text-lede">{lede}</p>}
        </div>
        {/*
          **Une rangée qui défile plutôt qu'une pile** (US-105). Sur un
          téléphone, `flex-wrap` empilait quatre destinations sur quatre
          rangées — deux cent cinquante pixels de liens avant la première
          recette, en escalier, parce qu'aucune ne faisait la même largeur.
          Elles tiennent maintenant sur une ligne qu'on pousse du doigt.

          `-mx-6 px-6` : la rangée déborde les gouttières de la page pour que
          le défilement aille d'un bord à l'autre — sinon le dernier lien
          semble coupé par une marge, et l'on ne devine pas qu'il y en a
          d'autres. `[scrollbar-width:none]` : une barre de défilement dans
          une rangée de boutons de 44 px la ferait paraître cassée.
        */}
        {actions !== undefined && (
          <div className="-mx-6 flex shrink-0 items-center gap-2 overflow-x-auto px-6 [scrollbar-width:none] md:mx-0 md:flex-wrap md:overflow-visible md:px-0">
            {actions}
          </div>
        )}
      </div>

      <hr className="rule" />

      {children}
    </header>
  );
}
